import { z } from 'zod';

// Setter spec §09.7–§09.10 + UAT-54 (§12.6): which media / language components a project can really use. Computed at
// read time from the real configuration (transcription provider, model assignments, voice persona, voice service) —
// never stored, never «verificado» by configuration alone. «Configurado · sin verificar» is the best state a component
// reaches here until a real Phase 0 test is recorded elsewhere.

export const MEDIA_COMPONENTS = ['voice_note_transcription', 'image_understanding', 'pdf_understanding', 'bilingual_whatsapp', 'bilingual_voice', 'english_voice'] as const;
export const MediaComponentSchema = z.enum(MEDIA_COMPONENTS);
export type MediaComponent = z.infer<typeof MediaComponentSchema>;

export const MEDIA_COMPONENT_LABELS: Record<MediaComponent, string> = {
  voice_note_transcription: 'Transcripción de notas de voz (WhatsApp)',
  image_understanding: 'Lectura de imágenes recibidas',
  pdf_understanding: 'Lectura de PDF recibidos',
  bilingual_whatsapp: 'Atención en inglés por WhatsApp',
  bilingual_voice: 'Cambio de idioma en llamadas',
  english_voice: 'Voz en inglés (texto a voz)',
};

export const MEDIA_COMPONENT_SPEC_REFS: Record<MediaComponent, string> = {
  voice_note_transcription: '§09.7',
  image_understanding: '§09.8',
  pdf_understanding: '§09.8',
  bilingual_whatsapp: '§09.9',
  bilingual_voice: '§09.9',
  english_voice: '§09.9 · §09.10',
};

/**
 *  • configured  — the component has what it needs to run; real quality is «sin verificar» (Phase 0).
 *  • degraded    — runs with a known limitation (e.g. no dedicated English voice).
 *  • unavailable — cannot run: the AI says it cannot process it / derives to a person.
 *  • disabled    — turned off on purpose in the project's configuration.
 */
export const MEDIA_COMPONENT_STATES = ['configured', 'degraded', 'unavailable', 'disabled'] as const;
export const MediaComponentStateSchema = z.enum(MEDIA_COMPONENT_STATES);
export type MediaComponentState = z.infer<typeof MediaComponentStateSchema>;
export const MEDIA_COMPONENT_STATE_LABELS: Record<MediaComponentState, string> = {
  configured: 'Configurado · sin verificar',
  degraded: 'Degradado',
  unavailable: 'No disponible',
  disabled: 'Desactivado',
};

export const MediaComponentStatusSchema = z.object({
  key: MediaComponentSchema,
  label: z.string(),
  channel: z.enum(['whatsapp', 'voice']),
  specRef: z.string(),
  state: MediaComponentStateSchema,
  /** What was found (or what is missing) and what the AI does meanwhile. */
  detail: z.string(),
});
export type MediaComponentStatus = z.infer<typeof MediaComponentStatusSchema>;

export const ProjectMediaCapabilitiesSchema = z.object({
  projectId: z.string(),
  components: z.array(MediaComponentStatusSchema),
  /** Components that are unavailable or degraded (disabled ones are a decision, not a gap). */
  gaps: z.number().int(),
  checkedAt: z.string(),
  verification: z.string(),
});
export type ProjectMediaCapabilities = z.infer<typeof ProjectMediaCapabilitiesSchema>;

export const MEDIA_CAPABILITIES_VERIFICATION_NOTE =
  'Calculado de la configuración actual (proveedor de transcripción, modelos asignados, voz del proyecto). Ningún componente se marca como verificado por estar configurado: la calidad real se comprueba en la Fase 0.';
